import { useEffect, useState, useCallback } from "react";
import { X, CaretLeft, CaretRight, Flag } from "@phosphor-icons/react";
import { photoSignedUrl, formatPhotoDate } from "@/lib/photos";
import PhotoReportSheet from "@/components/photos/PhotoReportSheet";

/**
 * Full-screen lightbox for a list of photos. Supports keyboard nav
 * (←/→/Esc), click-outside to close, and lazy signed-URL resolution.
 *
 * When `canReport` is true (parent surface), a flag button opens
 * PhotoReportSheet which calls rpc_report_child_photo. On success we
 * notify the caller via `onReported(photoId)` so the parent can optimistically
 * drop the photo from the local list (RLS hides flagged photos from parents).
 */
export default function PhotoLightbox({
  photos,
  initialIndex = 0,
  onClose,
  childrenById = {},
  canReport = false,
  onReported,
}) {
  const [index, setIndex] = useState(initialIndex);
  const [url, setUrl] = useState(null);
  const [errored, setErrored] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  const photo = photos?.[index];
  const photoStoragePath = photo?.storage_path;

  useEffect(() => {
    if (!photoStoragePath) return;
    let alive = true;
    setErrored(false);
    (async () => {
      try {
        // Lightbox: request a 1600px @ q=82 render — saves bandwidth on
        // phones while staying crisp enough for full-screen viewing.
        // onError below falls back to the original bytes if the
        // transform endpoint refuses (e.g. plan doesn't ship transforms).
        const u = await photoSignedUrl(photoStoragePath, {
          width: 1600,
          quality: 82,
        });
        if (alive) setUrl(u);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, [photoStoragePath]);

  // Pre-warm signed URLs for the neighbors so prev/next feels instant.
  // Failures are silent — the on-screen photo doesn't depend on this.
  useEffect(() => {
    if (!photos || photos.length < 2) return;
    const next = photos[(index + 1) % photos.length];
    const prev = photos[(index - 1 + photos.length) % photos.length];
    [next, prev].forEach((p) => {
      if (p?.storage_path) {
        photoSignedUrl(p.storage_path, { width: 1600, quality: 82 }).catch(() => {});
      }
    });
  }, [index, photos]);

  async function onImageError() {
    if (errored || !photo) return;
    setErrored(true);
    try {
      const original = await photoSignedUrl(photo.storage_path);
      if (original) setUrl(original);
    } catch {
      /* leave skeleton */
    }
  }

  const close = useCallback(() => onClose?.(), [onClose]);
  const prev = useCallback(
    () => setIndex((i) => (i > 0 ? i - 1 : photos.length - 1)),
    [photos?.length],
  );
  const next = useCallback(
    () => setIndex((i) => (i < photos.length - 1 ? i + 1 : 0)),
    [photos?.length],
  );

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, prev, next]);

  if (!photo) return null;

  const child = childrenById[photo.child_id];
  const childName = child ? `${child.first_name} ${child.last_name || ""}`.trim() : "";

  return (
    <div
      data-testid="photo-lightbox"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
      onClick={close}
    >
      <button
        type="button"
        data-testid="photo-lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
      >
        <X size={18} weight="bold" />
      </button>

      {canReport && (
        <button
          type="button"
          data-testid="photo-lightbox-report"
          onClick={(e) => {
            e.stopPropagation();
            setReportOpen(true);
          }}
          aria-label="Report this photo"
          className="absolute left-4 top-4 z-10 flex h-10 items-center gap-1.5 rounded-full bg-white/15 px-3 text-[12px] font-semibold text-white backdrop-blur-sm transition hover:bg-white/25"
        >
          <Flag size={14} weight="bold" />
          Report
        </button>
      )}

      {photos.length > 1 && (
        <>
          <button
            type="button"
            data-testid="photo-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
            className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
          >
            <CaretLeft size={20} weight="bold" />
          </button>
          <button
            type="button"
            data-testid="photo-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
            className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
          >
            <CaretRight size={20} weight="bold" />
          </button>
        </>
      )}

      <div
        className="flex max-h-full max-w-5xl flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {url ? (
          <img
            src={url}
            alt={photo.caption || "Child photo"}
            decoding="async"
            onError={onImageError}
            className="max-h-[80vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl"
          />
        ) : (
          <div className="h-[60vh] w-[80vw] animate-pulse rounded-2xl bg-white/10" />
        )}
        <div
          data-testid="photo-lightbox-meta"
          className="flex max-w-xl flex-col items-center gap-1 text-center text-white"
        >
          {photo.caption && (
            <p className="text-base font-medium leading-snug">{photo.caption}</p>
          )}
          <p className="text-xs text-white/70">
            {childName && <span>{childName} · </span>}
            {formatPhotoDate(photo.taken_at)}
            {photos.length > 1 && (
              <>
                {" · "}
                {index + 1} of {photos.length}
              </>
            )}
          </p>
        </div>
      </div>

      {reportOpen && (
        <PhotoReportSheet
          photoId={photo.id}
          onClose={() => setReportOpen(false)}
          onSuccess={(reportedId) => {
            setReportOpen(false);
            onReported?.(reportedId);
            close();
          }}
        />
      )}
    </div>
  );
}
